import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { SplitButton } from 'primereact/splitbutton';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { withRouter } from 'react-router-dom';
import SurveyDetailCore from './SurveyDetailCore';
import SurveyEditCoreForm from './SurveyEditCore';
import SurveyRespondents from './SurveyRespondents';
import SurveyReport from './SurveyReport';
import { DeleteTextButton } from '../../../components/button/ActionButton';
import ConfirmModal from './SurveyDeleteConfirmModal';
import { AppConstants } from '../../../shared/Constants';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import ArrayUtil from '../../../utils/ArrayUtil';
import {
  inviteAllRespondentsTakeSurvey,
  remindsAllRespondentsTakeSurvey,
  updateSurveyCore,
  removeSurvey,
} from '../../../services/apps/SurveyService';

const messages = defineMessages({
  survey: {
    id: 'survey',
    defaultMessage: 'Survey',
  },
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  sendInvite: {
    id: 'assessment.invite.send.plural',
    defaultMessage: 'Send Invites',
  },
  sendRemind: {
    id: 'assessment.remind.send.plural',
    defaultMessage: 'Send Reminders',
  },
});

const getRespondentStatuses = (self, managers, peers, directReports, others) => {
  const respondentStatuses = [];

  if (self) {
    respondentStatuses.push(self.status);
  }

  if (ArrayUtil.isNotEmpty(managers)) {
    managers.forEach((asmt) => {
      if (asmt) {
        respondentStatuses.push(asmt.status);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(others)) {
    others.forEach((asmt) => {
      if (asmt) {
        respondentStatuses.push(asmt.status);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(peers)) {
    peers.forEach((asmt) => {
      if (asmt) {
        respondentStatuses.push(asmt.status);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(directReports)) {
    directReports.forEach((asmt) => {
      if (asmt) {
        respondentStatuses.push(asmt.status);
      }
    });
  }

  return respondentStatuses;
};

const getRespondetArrId = records => Array.from(records, rec => rec.respondent.id);

/**
 * return the list of respondent who submitted the survey
 * @param {Object} survey
 */
const getSubmittedSurveyRespondent = (survey) => {
  const reportObservers = [];
  const {
    managers, others, peers, directReports,
  } = survey;

  // If the lists have the submitted survey assessment
  if (ArrayUtil.isNotEmpty(managers)) {
    managers.forEach((asmt) => {
      if (asmt.status === 'SUBMITTED' && asmt.respondent) {
        reportObservers.push(asmt.respondent.id);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(others)) {
    others.forEach((asmt) => {
      if (asmt.status === 'SUBMITTED' && asmt.respondent) {
        reportObservers.push(asmt.respondent.id);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(peers)) {
    peers.forEach((asmt) => {
      if (asmt.status === 'SUBMITTED' && asmt.respondent) {
        reportObservers.push(asmt.respondent.id);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(directReports)) {
    directReports.forEach((asmt) => {
      if (asmt.status === 'SUBMITTED' && asmt.respondent) {
        reportObservers.push(asmt.respondent.id);
      }
    });
  }

  return reportObservers;
};

class SurveyDetailDisplay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reportObservers: [],
      showDeleteConfirmModal: false,
      editCore: false,
      updating: false,
      isSending: false,
    };
    this.onChangeCheckedRespondent = this.onChangeCheckedRespondent.bind(this);
    this.deleteSurvey = this.deleteSurvey.bind(this);
    this.cancelDeletingSurvey = this.cancelDeletingSurvey.bind(this);
    this.processDeletingSurvey = this.processDeletingSurvey.bind(this);
    this.editSurveyCore = this.editSurveyCore.bind(this);
    this.updateSurveyCore = this.updateSurveyCore.bind(this);
    this.handleSendRemind = this.handleSendRemind.bind(this);
    this.handleSendInvitation = this.handleSendInvitation.bind(this);
    this.buildEmailButton = this.buildEmailButton.bind(this);
  }

  async componentDidMount() {
    this.initReportObserverList();
  }

  onChangeCheckedRespondent(event) {
    const { reportObservers } = this.state;
    const newRespondents = reportObservers.slice();
    if (event.checked) {
      newRespondents.push(event.target.value);
    } else {
      newRespondents.splice(reportObservers.indexOf(event.target.value), 1);
    }
    this.setState({ reportObservers: newRespondents });
  }

  async handleSendInvitation() {
    const {
      match, growl, intl, currentAccount,
    } = this.props;
    const { isSending } = this.state;
    const { formatMessage } = intl;

    this.setState({ isSending: !isSending });

    try {
      await inviteAllRespondentsTakeSurvey({
        surveyId: match.params.surveyId,
        accountId: currentAccount,
      });
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }

    this.setState({ isSending });
  }

  async handleSendRemind() {
    const {
      match, growl, intl, currentAccount,
    } = this.props;
    const { isSending } = this.state;
    const { formatMessage } = intl;

    this.setState({ isSending: !isSending });

    try {
      await remindsAllRespondentsTakeSurvey({
        surveyId: match.params.surveyId,
        accountId: currentAccount,
      });
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }

    this.setState({ isSending });
  }

  initReportObserverList() {
    const {
      data: { account },
    } = this.props;
    if (account && account.survey) {
      const reportObservers = getSubmittedSurveyRespondent(account.survey);
      this.setState({ reportObservers });
    }
  }

  deleteSurvey() {
    this.setState({ showDeleteConfirmModal: true });
  }

  cancelDeletingSurvey() {
    this.setState({ showDeleteConfirmModal: false });
  }

  async processDeletingSurvey(formValues) {
    const {
      match,
      history,
      currentAccount,
      growl,
      intl: { formatMessage },
    } = this.props;

    const { surveyId } = match.params ? match.params : null;
    const { confirmation_code, re_confirmation_code } = formValues;
    if (surveyId && confirmation_code === re_confirmation_code) {
      try {
        await removeSurvey({
          accountId: currentAccount,
          surveyId,
        });
        growlActionSuccess(growl, formatMessage(messages.successNoti));
        const redirectUrl = `/accounts/${currentAccount}/surveys`;
        history.push(redirectUrl);
      } catch (errors) {
        growlErrors(growl, errors);
      }
    }
    this.setState({ showDeleteConfirmModal: false });
  }

  editSurveyCore() {
    const { editCore } = this.state;
    this.setState({ editCore: !editCore });
  }

  buildEmailButton() {
    const {
      data: { account },
      intl: { formatMessage },
    } = this.props;

    const { isSending } = this.state;

    const { survey } = account;

    if (account && survey) {
      const {
        self, managers, others, peers, directReports,
      } = survey;

      const respondentStatuses = getRespondentStatuses(self, managers, peers, directReports, others);

      const onlyInvitesCanBeSent = respondentStatuses.includes('NEW')
        && !respondentStatuses.includes('INVITED')
        && !respondentStatuses.includes('STARTED');

      const onlyRemindersCanBeSent = (respondentStatuses.includes('INVITED')
      || respondentStatuses.includes('STARTED')) && !respondentStatuses.includes('NEW');

      const invitesAndRemindersCanBeSent = respondentStatuses.includes('NEW')
        && (respondentStatuses.includes('INVITED') || respondentStatuses.includes('STARTED'));

      if (onlyInvitesCanBeSent) {
        return (
          <Button
            label={formatMessage(messages.sendInvite)}
            icon="pi pi-envelope"
            onClick={this.handleSendInvitation}
            disabled={isSending}
          />
        );
      } if (onlyRemindersCanBeSent) {
        return (
          <Button
            label={formatMessage(messages.sendRemind)}
            icon="pi pi-envelope"
            onClick={this.handleSendRemind}
            disabled={isSending}
          />
        );
      } if (invitesAndRemindersCanBeSent) {
        const menu = [
          {
            label: formatMessage(messages.sendInvite),
            command: () => {
              this.handleSendInvitation();
            },
          },
          {
            label: formatMessage(messages.sendRemind),
            command: () => {
              this.handleSendRemind();
            },
          },
        ];

        return (
          <SplitButton
            label={formatMessage(messages.sendInvite)}
            icon="pi pi-envelope"
            onClick={this.handleSendInvitation}
            model={menu}
            disabled={isSending}
          />
        );
      }
    }

    return null;
  }

  async updateSurveyCore(formValues) {
    const {
      growl,
      data,
      currentAccount,
      intl: { formatMessage },
    } = this.props;
    const { survey } = data.account;

    if (survey) {
      const surveyId = survey.id;
      const { name, tags, commentsEnabled } = formValues;
      const managerIds = survey.managers ? getRespondetArrId(survey.managers) : [];
      const peerIds = survey.peers ? getRespondetArrId(survey.peers) : [];
      const directReportIds = survey.directReports ? getRespondetArrId(survey.directReports) : [];
      const otherIds = survey.others ? getRespondetArrId(survey.others) : [];

      try {
        const input = {
          accountId: currentAccount,
          surveyId,
          name,
          tags,
          commentsEnabled,
          managerIds,
          peerIds,
          directReportIds,
          otherIds,
        };
        await updateSurveyCore(input);
        growlActionSuccess(growl, formatMessage(messages.successNoti));
      } catch (errors) {
        growlErrors(growl, errors);
      }
    }

    this.setState({ editCore: false });
  }

  render() {
    const {
      data,
      translatedTags,
      currentAccount,
      match,
      intl: { formatMessage },
      locale,
    } = this.props;

    const availableLang = translatedTags.find(transLocale => locale === transLocale.tag);

    const viewerLocale = availableLang ? locale : AppConstants.DEFAULT_LANG;
    const {
      showDeleteConfirmModal, editCore, updating, reportObservers,
    } = this.state;
    const entityType = formatMessage(messages.survey);
    const { survey } = data.account;
    const initialValues = {
      name: survey.name,
      tags: survey.tags,
      commentsEnabled: survey.commentsEnabled,
    };

    const button = this.buildEmailButton();

    return (
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <div className="p-grid">
              <div className="p-sm-12 p-md-6">
                {editCore ? (
                  <Panel header={`${survey.self.respondent.firstName} ${survey.self.respondent.lastName}`}>
                    <SurveyEditCoreForm
                      initialValues={initialValues}
                      submitting={updating}
                      onSave={this.updateSurveyCore}
                      onCancel={this.editSurveyCore}
                    />
                  </Panel>
                ) : (
                  <SurveyDetailCore toggleEdit={this.editSurveyCore} survey={survey} />
                )}
              </div>
              <div className="p-sm-12 p-md-6 align-right">
                {button}
                {' '}
                <DeleteTextButton
                  position="right"
                  onClick={this.deleteSurvey}
                  text={formatMessage({ id: 'lbl_delete', defaultMessage: 'Delete {entity}' }, { entity: entityType })}
                />
              </div>

              <div className="p-sm-12 p-md-6">
                <SurveyRespondents
                  onChangeCheckedRespondent={this.onChangeCheckedRespondent}
                  survey={survey}
                  match={match}
                  reportObservers={reportObservers}
                />
              </div>
              <div className="p-sm-12 p-md-6">
                <SurveyReport
                  respondents={reportObservers}
                  survey={survey}
                  locales={translatedTags}
                  viewerLocale={viewerLocale}
                  currentAccount={currentAccount}
                />
              </div>
            </div>

            <ConfirmModal
              modalOpen={showDeleteConfirmModal}
              reject={this.cancelDeletingSurvey}
              confirm={this.processDeletingSurvey}
              typeButton="btn-danger"
              match={match}
            />
          </div>
        </div>
      </div>
    );
  }
}

SurveyDetailDisplay.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }).isRequired,
  locale: PropTypes.string.isRequired,
  match: PropTypes.objectOf(PropTypes.any).isRequired,
  translatedTags: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default withRouter(withGrowl(injectIntl(SurveyDetailDisplay)));
